import PropTypes from 'prop-types';
import React from 'react';
import { VaLink } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { buildDateFormatter } from '~/platform/shared/claims-appeals';
import * as TrackedItem from '../../utils/trackedItemContent';
import AddFilesForm from '../claim-files-tab/AddFilesForm';
import OtherWaysToSendYourDocuments from '../claim-files-tab-v2/OtherWaysToSendYourDocuments';
import { resolveSharedContent } from './shared/descriptionContent';
import RespondedOrPastDueRequestPage from './RespondedOrPastDueRequestPage';
import RequestNotifications from './shared/RequestNotifications';
import BasicLink from '../web-component-wrappers/BasicLink';
import { TRACKED_ITEMS_4142 } from '../../constants';
import { claimTabLinkProps } from '../../utils/claim-tab-helpers';
import {
  hasSuspenseDatePassed,
  showOriginalRequestPage,
} from '../../utils/trackedItemProcessing';

export default function FirstPartyRequestPage(props) {
  const {
    acceptedFileTypes,
    closedTrackedItemsEnabled,
    claimId,
    documentTypes,
    item,
    onCancel,
    onSubmit,
    progress,
    uploadResults,
    uploading,
    message,
    type1UnknownErrors,
    useVaTabs = false,
  } = props;

  const dateFormatter = buildDateFormatter();
  const pastDueDate = hasSuspenseDatePassed(item);
  const is4142TrackedItem = TRACKED_ITEMS_4142.includes(item?.displayName);
  const showUploadForm = !is4142TrackedItem && item.canUploadFile;
  const isSensitive = TrackedItem.getIsSensitive(item);
  const showRespondedOrPastDuePage =
    closedTrackedItemsEnabled && showOriginalRequestPage(item);

  const {
    longDescriptionContent: sharedLongDescription,
    longDescriptionTestId: sharedLongDescriptionTestId,
    nextStepsContent: sharedNextSteps,
    hasDescriptionContent,
  } = resolveSharedContent(item, {
    removeNextStepsLastMargin: showRespondedOrPastDuePage,
  });

  const getDisplayName = () => {
    if (isSensitive) {
      return TrackedItem.REQUEST_FOR_EVIDENCE_LABEL;
    }

    return item.friendlyName || TrackedItem.REQUEST_FOR_EVIDENCE_LABEL;
  };

  const getRequestText = () => {
    const prefix = closedTrackedItemsEnabled ? 'Requested by' : 'Respond by';

    if (item.friendlyName && isSensitive) {
      return `${prefix} ${dateFormatter(
        item.suspenseDate,
      )} for: ${TrackedItem.getDisplayFriendlyName(item)}`;
    }

    if (item.friendlyName) {
      return `${prefix} ${dateFormatter(item.suspenseDate)}`;
    }

    return `${prefix} ${dateFormatter(item.suspenseDate)} for: ${
      item.displayName
    }`;
  };

  // Handle first-party fallback for longDescription (empty state)
  let longDescriptionContent = sharedLongDescription;
  let longDescriptionTestId = sharedLongDescriptionTestId;

  if (!longDescriptionContent) {
    longDescriptionContent = (
      <>
        <p>
          We’re unable to provide more information about the request on this
          page. To learn more about it, review your claim letter.
        </p>
        <BasicLink
          text="Access your claim letters"
          label="Access your claim letters"
          path="/track-claims/your-claim-letters"
        />
      </>
    );
    longDescriptionTestId = 'empty-state-description';
  }

  const getNextStepsContent = () => {
    // Handle first-party fallback for nextSteps (generic instructions)
    let nextStepsContent = sharedNextSteps;

    if (!nextStepsContent) {
      nextStepsContent = (
        <>
          <p>To respond to this request:</p>
          <ul className="bullet-disc">
            {hasDescriptionContent ? (
              <li data-testid="next-steps-in-what-we-need-from-you">
                Gather and submit any documents or forms listed in the{' '}
                <strong>What we need from you</strong> section
              </li>
            ) : (
              <li data-testid="next-steps-in-claim-letter">
                Gather and submit any documents or forms listed in the claim
                letter
              </li>
            )}
            <li>You can upload documents online or mail them to us</li>
          </ul>
          {hasDescriptionContent && (
            <p>
              If you need help understanding this request, check your claim
              letter online.
              <br />
              <BasicLink
                text="Access your claim letters"
                label="Access your claim letters"
                path="/track-claims/your-claim-letters"
              />
            </p>
          )}
          <p
            className={
              showRespondedOrPastDuePage ? 'vads-u-margin-bottom--0' : undefined
            }
          >
            You can find blank copies of many VA forms online.
            <br />
            <VaLink
              text="Find a VA form"
              label="Find a VA form"
              href="/find-forms"
            />
          </p>
        </>
      );
    }

    const NextStepsHeader = showRespondedOrPastDuePage ? 'h4' : 'h2';

    return (
      <div
        className={
          showRespondedOrPastDuePage
            ? 'vads-u-margin-top--4'
            : 'vads-u-margin-y--4'
        }
      >
        <NextStepsHeader className="vads-u-margin-top--0 vads-u-margin-bottom--2">
          Next steps
        </NextStepsHeader>
        {nextStepsContent}
      </div>
    );
  };

  // Shared constants for open & closed display
  const pageTitle = getDisplayName();

  const getRequestContent = () => {
    const LearnHeader = showRespondedOrPastDuePage ? 'h4' : 'h3';

    return (
      <>
        {longDescriptionContent && (
          <div
            className="vads-u-margin-bottom--4"
            data-testid={longDescriptionTestId}
          >
            {longDescriptionContent}
          </div>
        )}
        {hasDescriptionContent && !item.hideClaimLetterSection && (
          <div
            className="vads-u-margin-y--4"
            data-testid="learn-about-request-section"
          >
            <LearnHeader className="vads-u-margin-top--0 vads-u-margin-bottom--2">
              Learn about this request in your claim letter
            </LearnHeader>
            <p className="vads-u-margin-y--2">
              On {dateFormatter(item.requestedDate)}, we mailed you a letter
              titled "Request for Specific Evidence or Information," which may
              include more details about this request.
            </p>
            <p className="vads-u-margin-top--2 vads-u-margin-bottom--0">
              You can access this and all your claim letters online.
            </p>
            <BasicLink
              text="Access your claim letters"
              label="Access your claim letters"
              path="/track-claims/your-claim-letters"
            />
          </div>
        )}
      </>
    );
  };

  const summaryBoxList = () => (
    <ul>
      <li>
        <strong>Check which files you uploaded.</strong>
        <br />
        <BasicLink
          text="Review files you submitted"
          {...(useVaTabs
            ? claimTabLinkProps('files', { claimId })
            : { path: `/your-claims/${claimId}/files` })}
        />
      </li>
      <li>
        <strong>Upload additional documents.</strong> You don’t need to send
        anything else, but you can add more evidence if you have it. This may
        delay your claim while we review it.
      </li>
    </ul>
  );

  if (showRespondedOrPastDuePage) {
    return (
      <RespondedOrPastDueRequestPage
        nextStepsContent={getNextStepsContent()}
        requestContent={getRequestContent()}
        pageTitle={pageTitle}
        requestText={getRequestText()}
        showUploadForm={showUploadForm}
        summaryBoxList={summaryBoxList()}
        {...props}
      />
    );
  }

  return (
    <div id="default-page" className="vads-u-margin-bottom--3">
      <div className="vads-u-margin-bottom--4">
        <h1 className="claims-header">
          {pageTitle}
          <span className="vads-u-font-family--sans vads-u-margin-top--0p5">
            {getRequestText()}
          </span>
        </h1>
      </div>
      <RequestNotifications
        message={message}
        trackedItem={item}
        type1UnknownErrors={type1UnknownErrors}
        useVaTabs={useVaTabs}
      />
      {pastDueDate && (
        <va-alert status="warning" class="vads-u-margin-y--4">
          <h2
            slot="headline"
            className="vads-u-margin-top--0 vads-u-margin-bottom--2"
          >
            The deadline to send us information passed
          </h2>
          <p className="vads-u-margin-y--0">
            We haven’t received the information we asked for. You can still send
            it, but it may delay your claim.
          </p>
          <p>
            If you have questions, call the VA benefits hotline at{' '}
            <va-telephone contact="8008271000" /> (
            <va-telephone contact="711" tty="true" />
            ).
          </p>
        </va-alert>
      )}
      <div className="vads-u-margin-top--4 vads-u-margin-bottom--2">
        <p>
          We requested this evidence from you on{' '}
          {dateFormatter(item.requestedDate)}. You can still send the evidence
          after the “respond by” date, but it may delay your claim.
        </p>
      </div>
      <h2 className="vads-u-margin-top--2 vads-u-margin-bottom--2">
        What we need from you
      </h2>
      {getRequestContent()}
      {getNextStepsContent()}
      {showUploadForm && (
        <>
          <AddFilesForm
            useVaTabs={useVaTabs}
            acceptedFileTypes={acceptedFileTypes}
            documentTypes={documentTypes}
            hideOtherWaysLink
            progress={progress}
            uploadResults={uploadResults}
            uploading={uploading}
            onCancel={onCancel}
            onSubmit={files => onSubmit(files)}
          />
          <div className="vads-u-margin-top--4">
            <OtherWaysToSendYourDocuments />
          </div>
        </>
      )}
    </div>
  );
}

FirstPartyRequestPage.propTypes = {
  claimId: PropTypes.string.isRequired,
  closedTrackedItemsEnabled: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.array,
  documentTypes: PropTypes.array,
  message: PropTypes.object,
  progress: PropTypes.number,
  type1UnknownErrors: PropTypes.array,
  uploadResults: PropTypes.object,
  uploading: PropTypes.bool,
  useVaTabs: PropTypes.bool,
};
